/**
 * Firestore CRUD for executiveCalendarEventGroups.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import type { CalendarEventGroup, CalendarEventGroupSegment } from '../types';

const COLLECTION = 'executiveCalendarEventGroups';

export function newEventGroupId(): string {
  return `grp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function parseGroup(id: string, data: Record<string, unknown>): CalendarEventGroup {
  return {
    id,
    executiveUid: data.executiveUid as string,
    anchorMode: data.anchorMode as CalendarEventGroup['anchorMode'],
    anchorSegmentIndex: data.anchorSegmentIndex as number,
    segments: (data.segments as CalendarEventGroupSegment[]) || [],
    invitees: Array.isArray(data.invitees)
      ? (data.invitees as unknown[]).filter((e): e is string => typeof e === 'string')
      : undefined,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
  };
}

export async function loadEventGroup(groupId: string): Promise<CalendarEventGroup | null> {
  const snap = await getDoc(doc(db, COLLECTION, groupId));
  if (!snap.exists()) return null;
  return parseGroup(snap.id, snap.data() as Record<string, unknown>);
}

export async function loadEventGroupsForExecutive(
  executiveUid: string,
): Promise<CalendarEventGroup[]> {
  const q = query(collection(db, COLLECTION), where('executiveUid', '==', executiveUid));
  const snap = await getDocs(q);
  return snap.docs.map(d => parseGroup(d.id, d.data() as Record<string, unknown>));
}

/** Live subscription — fires when the agent or CRM mutates linked event groups. */
export function subscribeEventGroupsForExecutive(
  executiveUid: string,
  cb: (groups: CalendarEventGroup[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, COLLECTION), where('executiveUid', '==', executiveUid));
  return onSnapshot(
    q,
    snap => {
      cb(snap.docs.map(d => parseGroup(d.id, d.data() as Record<string, unknown>)));
    },
    err => onError?.(err instanceof Error ? err : new Error(String(err))),
  );
}

export async function saveEventGroup(group: CalendarEventGroup): Promise<string> {
  const id = group.id || newEventGroupId();
  const now = Timestamp.now();
  await setDoc(doc(db, COLLECTION, id), {
    executiveUid: group.executiveUid,
    anchorMode: group.anchorMode,
    anchorSegmentIndex: group.anchorSegmentIndex,
    segments: group.segments,
    createdAt: group.createdAt ? Timestamp.fromDate(group.createdAt) : now,
    updatedAt: now,
  });
  return id;
}

export async function updateEventGroupSegments(
  groupId: string,
  segments: CalendarEventGroupSegment[],
): Promise<void> {
  await setDoc(
    doc(db, COLLECTION, groupId),
    {
      segments,
      updatedAt: Timestamp.now(),
    },
    { merge: true },
  );
}

export async function deleteEventGroup(groupId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, groupId));
}

export function findGroupByGoogleEventId(
  groups: CalendarEventGroup[],
  googleEventId: string,
): CalendarEventGroup | undefined {
  return groups.find(g => g.segments.some(s => s.googleEventId === googleEventId));
}

export function buildGroupMap(
  groups: CalendarEventGroup[],
): Map<string, CalendarEventGroup> {
  return new Map(groups.filter(g => g.id).map(g => [g.id!, g]));
}

export function buildEventIdToGroupMap(
  groups: CalendarEventGroup[],
): Map<string, CalendarEventGroup> {
  const map = new Map<string, CalendarEventGroup>();
  for (const g of groups) {
    for (const seg of g.segments) {
      if (seg.googleEventId) map.set(seg.googleEventId, g);
    }
  }
  return map;
}
